import { AxiosInstance } from 'axios';
import {
  IAccessiblePatient,
  IAccessiblePatientQuery,
  IPatientC1DataRequest,
  IPatientC1DataResult,
  IPatientDemographics,
  IPatientHistory,
  PatientGermlineConsent,
  IPatient,
  IPatientFilters,
  IBiomaterial,
  ISubmitAssayData,
  IClaritySubmission,
  AssaySubmissionResult,
} from '@zero-dash/types';

interface IUpdatePatient {
  comments?: string;
}

export interface IPatientSample {
  patientId: string;
  tumourId: string;
  rnaseqId: string;
  normalId: string;
  methId: string;
  donorId: string;
  zcc_subject_id: string;
}

export interface IPatientClient {
  getAccessiblePatients(query: IAccessiblePatientQuery): Promise<IAccessiblePatient[]>;
  getPatients(filters: IPatientFilters): Promise<IPatient[]>;
  getPatientById(patientId: string): Promise<IPatient>;
  updatePatient(
    patientId: string,
    data: IUpdatePatient,
  ): Promise<void>;
  getPatientHistory(patientId: string): Promise<IPatientHistory>;
  updatePatientHistory(
    patientId: string,
    clinicalHistory: string,
  ): Promise<void>;
  getPatientDemographics(patientId: string, eventNumber?: number): Promise<IPatientDemographics>;
  getPatientsC1Data(patientEvents: IPatientC1DataRequest[]): Promise<IPatientC1DataResult[]>;
  getPatientConsent(patientId: string): Promise<PatientGermlineConsent>;
  getPatientEvents(patientId: string): Promise<Pick<IPatientDemographics, 'events'> | null>;
  getBiomaterialsByLmSubjId(
    patientId: string,
    lmSubjectId: string,
  ): Promise<IBiomaterial[]>;
  getClaritySubmissions(
    patientId: string,
    lmSubjectId: string,
  ): Promise<IClaritySubmission[]>
  submitBiomaterialsAssays(
    patientId: string,
    lmSubjectId: string,
    assayData: ISubmitAssayData[],
    name?: string,
  ): Promise<AssaySubmissionResult[]>
}

export function createPatientClient(instance: AxiosInstance): IPatientClient {
  async function getAccessiblePatients(
    query: IAccessiblePatientQuery,
  ): Promise<IAccessiblePatient[]> {
    const resp = await instance.get<IAccessiblePatient[]>(
      '/patients/accessible',
      {
        params: query,
      },
    );

    return resp.data;
  }

  async function getPatients(filters: IPatientFilters): Promise<IPatient[]> {
    const resp = await instance.post<IPatient[]>(
      '/patients',
      { ...filters },
    );

    return resp.data;
  }

  async function getPatientById(patientId: string): Promise<IPatient> {
    const resp = await instance.get<IPatient>(`/patients/${patientId}`);

    return resp.data;
  }

  async function updatePatient(
    patientId: string,
    data: IUpdatePatient,
  ): Promise<void> {
    await instance.patch(`/patients/${patientId}`, data);
  }

  async function getPatientHistory(patientId: string): Promise<IPatientHistory> {
    const resp = await instance.get<IPatientHistory>(`/patients/${patientId}/history`);
    return resp.data;
  }

  async function updatePatientHistory(
    patientId: string,
    clinicalHistory: string,
  ): Promise<void> {
    await instance.patch(`/patients/${patientId}/history`, { clinicalHistory });
  }

  async function getPatientDemographics(
    patientId: string,
    eventNumber?: number,
  ): Promise<IPatientDemographics> {
    const resp = await instance.get<IPatientDemographics>(
      `/data-capture/patient/${patientId}/demographics`,
      {
        params: {
          eventNumber,
        },
      },
    );
    return resp.data;
  }

  async function getPatientsC1Data(
    patientEvents: IPatientC1DataRequest[],
  ): Promise<IPatientC1DataResult[]> {
    const resp = await instance.post<IPatientC1DataResult[]>(
      '/data-capture/patients/c1-data',
      { patientEvents },
    );
    return resp.data;
  }

  async function getPatientConsent(patientId: string): Promise<PatientGermlineConsent> {
    const resp = await instance.get<PatientGermlineConsent>(
      `/data-capture/patient/${patientId}/consent`,
    );

    return resp.data;
  }

  async function getPatientEvents(patientId: string): Promise<Pick<IPatientDemographics, 'events'> | null> {
    const resp = await instance.get<Pick<IPatientDemographics, 'events'> | null>(
      `/data-capture/patient/${patientId}/events`,
    );

    return resp.data;
  }

  async function getBiomaterialsByLmSubjId(
    patientId: string,
    lmSubjectId: string,
  ): Promise<IBiomaterial[]> {
    const resp = await instance.get<IBiomaterial[]>(`/data-capture/patient/${patientId}/${lmSubjectId}/biomaterials`);

    return resp.data;
  }

  async function getClaritySubmissions(
    patientId: string,
    lmSubjectId: string,
  ): Promise<IClaritySubmission[]> {
    const resp = await instance.get<IClaritySubmission[]>(
      `/data-capture/patient/${patientId}/${lmSubjectId}/submissions`,
    );

    return resp.data;
  }

  async function submitBiomaterialsAssays(
    patientId: string,
    lmSubjectId: string,
    assayData: ISubmitAssayData[],
    name?: string,
  ): Promise<AssaySubmissionResult[]> {
    const resp = await instance.post<AssaySubmissionResult[]>(
      `/data-capture/patient/${patientId}/${lmSubjectId}/assays`,
      { name, submissions: assayData },
    );

    return resp.data;
  }

  return {
    getAccessiblePatients,
    getPatients,
    getPatientById,
    updatePatient,
    getPatientHistory,
    updatePatientHistory,
    getPatientDemographics,
    getPatientsC1Data,
    getPatientConsent,
    getPatientEvents,
    getBiomaterialsByLmSubjId,
    getClaritySubmissions,
    submitBiomaterialsAssays,
  };
}
